import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, formatApiError } from "@/auth";
import { KeyRound, Loader2, CheckCircle2, Inbox } from "lucide-react";

const PLANS = [
  { id: "1M", label: "1 Mois", days: 30 },
  { id: "3M", label: "3 Mois", days: 90 },
  { id: "6M", label: "6 Mois", days: 180 },
  { id: "12M", label: "12 Mois", days: 365 },
];
const METHODS = [["airtel", "Airtel Money"], ["orange", "Orange Money"], ["binance", "Binance Pay"], ["visa", "Visa / Carte"]];
const EMPTY = { username: "", email: "", plan: "3M", amount: "", transaction_ref: "", method: "airtel", request_id: null };

export function ManualActivation() {
  const [form, setForm] = useState(EMPTY);
  const [prices, setPrices] = useState({});
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [result, setResult] = useState(null);

  const load = async () => {
    const [cfg, pays] = await Promise.all([
      axios.get(`${API}/merchant/config`),
      axios.get(`${API}/payments`, { params: { status: "contact_requested" } }),
    ]);
    setPrices(cfg.data); setRequests(pays.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const pickPlan = (p) => setForm(f => ({ ...f, plan: p, amount: prices[`price_${p.toLowerCase()}`] ?? f.amount }));
  const useRequest = (r) => setForm(f => ({
    ...f, username: r.user_name || "", email: r.user_email, request_id: r.id,
    amount: f.amount === "" ? (prices[`price_${f.plan.toLowerCase()}`] ?? "") : f.amount,
  }));

  const days = PLANS.find(p => p.id === form.plan)?.days || 0;
  const preview = new Date(Date.now() + days * 864e5).toLocaleDateString("fr-FR");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setMsg(null); setResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/licenses/manual-activate`, { ...form, amount: Number(form.amount) });
      setResult(data); setForm(EMPTY); setRequests(rs => rs.filter(r => r.id !== form.request_id)); load();
    } catch (err) { setMsg(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="manual-activation">
      <form onSubmit={submit} className="card-lhs p-5 lg:col-span-2 space-y-4" data-testid="manual-activation-form">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-orange-500" />
          <h2 className="font-display font-bold uppercase tracking-wider text-sm">Activation de licence LHSunlocker</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <In label="Nom d'utilisateur" v={form.username} on={v => set("username", v)} testid="ma-username" />
          <In label="Email du compte" type="email" v={form.email} on={v => set("email", v)} testid="ma-email" />
        </div>
        <div>
          <label className="block text-[11px] uppercase text-zinc-500 mb-1 font-mono-tech">Formule</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PLANS.map(p => (
              <button type="button" key={p.id} onClick={() => pickPlan(p.id)} data-testid={`ma-plan-${p.id}`}
                className={`rounded-lg border py-2 text-xs font-mono-tech uppercase transition-colors focus:outline-none ${form.plan === p.id ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <In label={`Montant (${prices.currency || "USD"})`} type="number" v={form.amount} on={v => set("amount", v)} testid="ma-amount" />
          <In label="Référence transaction" v={form.transaction_ref} on={v => set("transaction_ref", v)} testid="ma-txref" />
          <div>
            <label className="block text-[11px] uppercase text-zinc-500 mb-1 font-mono-tech">Moyen de paiement</label>
            <select data-testid="ma-method" className="input-lhs" value={form.method} onChange={e => set("method", e.target.value)}>
              {METHODS.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
            </select>
          </div>
        </div>
        <div className="text-xs text-zinc-500 font-mono-tech" data-testid="ma-expiry-preview">
          Durée : {days} jours · Expiration estimée : <span className="text-orange-400">{preview}</span> (prolongée si licence déjà active)
          {form.request_id && <span className="ml-2 text-emerald-400">· liée à une demande</span>}
        </div>
        {msg && <div className="text-sm text-red-400 border border-red-500/40 bg-red-500/10 rounded-lg px-3 py-2" data-testid="ma-error">{msg}</div>}
        {result && (
          <div className="text-sm text-emerald-400 border border-emerald-500/40 bg-emerald-500/10 rounded-lg px-3 py-2" data-testid="ma-success">
            <CheckCircle2 className="w-4 h-4 inline mr-1" />
            Licence {result.plan} activée pour {result.user.name} · Clé <span className="font-mono-tech">{result.license_key}</span> ·
            expire le {new Date(result.expires_at).toLocaleDateString("fr-FR")} · {result.email_sent ? "email envoyé" : "email non envoyé"}
          </div>
        )}
        <button data-testid="ma-submit" disabled={busy} className="btn-orange rounded-lg px-5 py-2.5 text-sm uppercase tracking-wider inline-flex items-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Activer la licence
        </button>
      </form>
      <RequestsList rows={requests} onUse={useRequest} />
    </div>
  );
}

function RequestsList({ rows, onUse }) {
  return (
    <div className="card-lhs p-5" data-testid="ma-requests">
      <div className="flex items-center gap-2 mb-3">
        <Inbox className="w-4 h-4 text-orange-500" />
        <h3 className="font-display font-bold uppercase tracking-wider text-sm">Demandes en attente</h3>
      </div>
      <div className="space-y-2 max-h-96 overflow-y-auto scroll-thin">
        {rows.length === 0 && <div className="text-sm text-zinc-500 italic">Aucune demande.</div>}
        {rows.map(r => (
          <div key={r.id} className="rounded-lg border border-zinc-800 bg-[#0C0C10] p-3 text-xs" data-testid={`ma-request-${r.id}`}>
            <div className="font-mono-tech text-orange-400">{r.reference}</div>
            <div className="text-zinc-300 mt-1 truncate">{r.user_name} · {r.user_email}</div>
            <button type="button" onClick={() => onUse(r)} data-testid={`ma-use-${r.id}`}
              className="mt-2 text-[11px] uppercase font-mono-tech text-orange-400 hover:text-orange-300">Pré-remplir →</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function In({ label, v, on, type = "text", testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase text-zinc-500 mb-1 font-mono-tech">{label}</label>
      <input data-testid={testid} required type={type} step="any" className="input-lhs" value={v} onChange={e => on(e.target.value)} />
    </div>
  );
}
